/**
 * Right-rail summary for the hotel checkout page. Renders the hotel name,
 * dates, per-room cards with rate-level pricing, the prepaid grand total,
 * and a separate "Charged at the property" section listing any room extras
 * the user has selected. Extras are post-stay incidentals settled at the
 * hotel — not part of the prepaid Sabre booking — so they're never folded
 * into room subtotals or the grand total.
 *
 * Works for both checkout modes: pass in the rooms + priceSnapshot directly,
 * regardless of whether they come from in-memory prep data or a persisted
 * HotelBooking doc.
 */

import type { Hotel } from '@elara/client-core/models/HotelModel';
import type { RoomOptionModel as RoomOption } from '@elara/client-core/models/RoomOptionModel';
import type { RoomRateModel } from '@elara/client-core/models/RoomRateModel';
import type {
  HotelBookingRoom,
  PriceSnapshotItem,
  RoomEligibleExtras,
} from '@elara/domain/models/schemas/booking-schemas';
import type { PaymentScheduleEntry } from '@elara/domain/models/schemas/itinerary-item-schemas';

import React from 'react';

import { aggregatePropertyExtras, computeTotalBreakdown, nightsBetween } from '@elara/client-core/lib/checkout-summary';
import { formatDateRange } from '@elara/client-core/lib/date-utils';
import { bookingOccupancyToRateOccupancy } from '@elara/client-core/lib/occupancy-utils';
import { cn, formatBookingOccupancy, formatPrice, pluralize } from '@elara/client-core/lib/utils';
import { combinePaymentSchedules, formatPaymentScheduleLabel } from '@elara/domain/payment-schedule';

import { AskElaraButton } from '#ui/AskElaraButton.js';
import { Badge } from '#ui/badge.js';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '#ui/dialog.js';
import { Alert, AlertDescription, AlertTitle, AppImage, Button } from '#ui/index.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';
import { CheckoutPolicyDetails } from '#views/checkout/CheckoutPolicyDetails.js';
import { getPaymentScheduleForRoom } from '#views/checkout/payment-schedule-utils.js';
import {
  describeCancellationPolicy,
  describeDepositPolicy,
  describeGuaranteePolicy,
} from '#views/checkout/policy-utils.js';

/**
 * A room's fate after a partly-booked order: booked, not booked (refused, gone, or never attempted), or
 * unconfirmed (the create may have gone through — the server says not to try again).
 */
export type RoomOutcome = 'booked' | 'notBooked' | 'unconfirmed';

/** One line of the price-change notice: which room, and what it was versus what it is now. */
export interface RoomPriceChangeLine {
  roomName: string;
  previousTotal?: { amount: number; currency: string };
  newTotal?: { amount: number; currency: string };
}

interface HotelBookingSummaryProps {
  hotel: Hotel | undefined;
  roomOptionsById: Map<string, RoomOption | undefined>;
  rooms?: HotelBookingRoom[];
  priceSnapshot?: PriceSnapshotItem[];
  checkInDate?: string;
  checkOutDate?: string;
  /**
   * Live form state for selected extras. Only used to render the
   * "Charged at the property" section — extras don't affect the
   * prepaid-booking subtotals or grand total.
   */
  formRooms?: Record<string, { extras: Record<string, number> }>;
  /**
   * Server-resolved eligible extras per room (keyed by itinerary item id). The
   * "Charged at the property" lines read name/amount/currency/scope from here
   * rather than the live rate's catalog, whose search-time entries carry no id.
   */
  eligibleExtras?: RoomEligibleExtras[];
  liveTotal?: { amount: number; currency: string };
  /**
   * Names of rooms that came back unavailable from PriceCheck and were
   * dropped from the checkout. Surfaced as a warning banner above the
   * Rooms section so the user knows what was excluded.
   */
  droppedRoomNames?: string[];
  /** Replaces the dropped-rooms sentence when the rooms were dropped for a stated count, e.g. "Only 1 Conrad Suite is available…". */
  droppedRoomsNote?: string;
  /** The notice's "Ask Elara" as a button: opens the trip's chat with the question about the dropped rooms drafted. */
  onAskElara?: () => void;
  /** Rooms still in the checkout whose price moved — shown as old → new, never as "unavailable". */
  priceChanges?: RoomPriceChangeLine[];
  /**
   * After a partly-booked order (no rollback — ELARA-1079): which rooms booked and which did not. Each row
   * is badged, a room that did not book shows no price, and the money lines cover only what booked.
   */
  roomOutcomes?: Record<string, RoomOutcome>;
}

const OUTCOME_BADGE: Record<RoomOutcome, { variant: 'success' | 'destructive' | 'secondary'; label: string }> = {
  booked: { variant: 'success', label: 'Booked' },
  notBooked: { variant: 'destructive', label: 'Not booked' },
  unconfirmed: { variant: 'secondary', label: 'Unconfirmed' },
};

export function HotelBookingSummary({
  hotel,
  roomOptionsById,
  rooms = [],
  priceSnapshot = [],
  checkInDate,
  checkOutDate,
  formRooms,
  eligibleExtras = [],
  liveTotal,
  droppedRoomNames = [],
  droppedRoomsNote,
  onAskElara,
  priceChanges = [],
  roomOutcomes,
}: HotelBookingSummaryProps): React.ReactNode {
  // Once the order is partly booked, the money lines count only the rooms that booked.
  const countedSnapshot = roomOutcomes
    ? priceSnapshot.filter((item) => roomOutcomes[item.itineraryItemId] === 'booked')
    : priceSnapshot;
  const snapshotTotal =
    countedSnapshot.length > 0
      ? {
          amount: countedSnapshot.reduce((sum, item) => sum + item.totalCost.amount, 0),
          currency: countedSnapshot[0]?.totalCost.currency ?? 'USD',
        }
      : undefined;
  const total = roomOutcomes ? snapshotTotal : (liveTotal ?? snapshotTotal);

  // Tax/fee/subtotal breakdown of the prepaid total, summed from the
  // server-computed per-room `priceSnapshot[].breakdown`. Re-summing here (vs.
  // using the flow-level `totalBreakdown`) keeps the lines correct after
  // unavailable rooms are dropped from the displayed set.
  const breakdown = computeTotalBreakdown(countedSnapshot);

  const propertyExtras = aggregatePropertyExtras(rooms, formRooms, roomOptionsById, eligibleExtras);

  // Per-room schedules folded into a single trip-level cashflow view.
  const paymentSchedule =
    checkInDate && checkOutDate
      ? combinePaymentSchedules(
          rooms.map((room) => {
            const roomOption = roomOptionsById.get(room.roomOptionId);
            const rate = roomOption?.getRateByIdentity({
              productCode: room.productCode,
              occupancy: bookingOccupancyToRateOccupancy(room.occupancy),
            });
            const snapshot = countedSnapshot.find((s) => s.itineraryItemId === room.itineraryItemId);
            return getPaymentScheduleForRoom({ rate, priceSnapshot: snapshot, checkInDate, checkOutDate });
          }),
        )
      : [];

  return (
    <div className="@container space-y-6">
      <section>
        <div className="flex items-center gap-4">
          {hotel?.imageUrl ? (
            <AppImage
              src={hotel.imageUrl}
              alt={hotel.name ?? 'Hotel'}
              className="h-14 w-14 shrink-0 rounded-sm object-cover"
            />
          ) : null}
          <div className="min-w-0">
            <p className="truncate text-base font-display">{hotel?.name ?? 'Hotel'}</p>
            {checkInDate && checkOutDate
              ? (() => {
                  const nights = nightsBetween(checkInDate, checkOutDate);
                  return (
                    <div className="mt-0.5 flex items-baseline gap-2 text-sm text-muted-foreground">
                      <span>{formatDateRange(checkInDate, checkOutDate)}</span>
                      <span>·</span>
                      <span>
                        {nights} {pluralize(nights, 'night')}
                      </span>
                    </div>
                  );
                })()
              : null}
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {pluralize(rooms.length, 'Room')}
        </h2>

        {priceChanges.length > 0 ? (
          <Alert variant="warning" className="flex gap-3 border border-amber-200">
            <MaterialIcon icon="info" size={IconSizes.sm} className="mt-0.5 shrink-0" />
            <div className="min-w-0">
              <AlertTitle>{priceChanges.length === 1 ? 'The price has changed' : 'Prices have changed'}</AlertTitle>
              <AlertDescription>
                <ul className="space-y-0.5">
                  {priceChanges.map((change, index) => (
                    <li key={`${change.roomName}-${index}`}>
                      <span className="font-medium">{change.roomName}</span>
                      {change.previousTotal && change.newTotal
                        ? `: was ${formatPrice(change.previousTotal.amount, change.previousTotal.currency)}, now ${formatPrice(change.newTotal.amount, change.newTotal.currency)}`
                        : change.newTotal
                          ? `: now ${formatPrice(change.newTotal.amount, change.newTotal.currency)}`
                          : ': the hotel re-priced this room'}
                    </li>
                  ))}
                </ul>
                <p className="mt-1">The total below shows the new price. Confirm to book at that price.</p>
              </AlertDescription>
            </div>
          </Alert>
        ) : null}

        {droppedRoomNames.length > 0 ? (
          <Alert variant="destructive" className="flex gap-3 border border-red-200">
            <MaterialIcon icon="error" size={IconSizes.sm} className="mt-0.5 shrink-0" />
            <div className="min-w-0">
              <AlertTitle>
                {droppedRoomNames.length === 1 ? 'A room is no longer available' : 'Some rooms are no longer available'}
              </AlertTitle>
              <AlertDescription>
                {droppedRoomsNote ?? (
                  <>
                    We've removed{' '}
                    {droppedRoomNames.length === 1 ? (
                      <span className="font-medium">{droppedRoomNames[0]}</span>
                    ) : (
                      <>
                        {droppedRoomNames.length} rooms ({droppedRoomNames.join(', ')})
                      </>
                    )}{' '}
                    from your booking. Ask Elara to find alternatives.
                  </>
                )}
              </AlertDescription>
              {/* The alert paints its text red and the outline button inherits it; the action reads as neutral. */}
              {onAskElara ? <AskElaraButton size="sm" onClick={onAskElara} className="mt-3 text-foreground" /> : null}
            </div>
          </Alert>
        ) : null}

        <div className="rounded-md border bg-background divide-y">
          {sortRoomsByName(rooms, roomOptionsById).map((room) => {
            const roomOption = roomOptionsById.get(room.roomOptionId);
            const rate = roomOption?.getRateByIdentity({
              productCode: room.productCode,
              occupancy: bookingOccupancyToRateOccupancy(room.occupancy),
            });
            const snapshot = priceSnapshot.find((s) => s.itineraryItemId === room.itineraryItemId);
            const outcome = roomOutcomes?.[room.itineraryItemId];
            // Per-room price excludes taxes & fees so it matches the Subtotal
            // line in the breakdown below. Uses the server-computed per-room
            // `breakdown.subtotal`; falls back to the all-in snapshot total when
            // a breakdown isn't present (e.g. pre-existing persisted bookings).
            const lineSubtotal = snapshot?.breakdown
              ? formatPrice(snapshot.breakdown.subtotal, snapshot.breakdown.currency)
              : snapshot
                ? formatPrice(snapshot.totalCost.amount, snapshot.totalCost.currency)
                : undefined;

            const thumbnailUrl = roomOption?.images?.[0]?.url;
            return (
              <React.Fragment key={room.itineraryItemId}>
                <div className="p-3 @md:p-4">
                  <div className="flex items-start gap-3">
                    {thumbnailUrl ? (
                      <AppImage
                        src={thumbnailUrl}
                        alt={roomOption?.displayName ?? 'Room'}
                        className="h-10 w-10 shrink-0 rounded-sm object-cover"
                      />
                    ) : (
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-background-tertiary text-muted-foreground/40">
                        <MaterialIcon icon="hotel" size={IconSizes.sm} />
                      </div>
                    )}
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <div className="flex min-w-0 items-baseline gap-2">
                          <p className="truncate text-sm font-medium">{roomOption?.displayName ?? 'Room'}</p>
                          {outcome ? (
                            <Badge variant={OUTCOME_BADGE[outcome].variant} size="sm">
                              {OUTCOME_BADGE[outcome].label}
                            </Badge>
                          ) : null}
                        </div>
                        {lineSubtotal && (!outcome || outcome === 'booked') ? (
                          <p className="shrink-0 text-sm font-medium tabular-nums">{lineSubtotal}</p>
                        ) : null}
                      </div>

                      <div className="flex flex-wrap gap-x-2.5 gap-y-1 items-center text-2xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <MaterialIcon icon="group" size={IconSizes.xxs} />
                          <span className="truncate">{formatBookingOccupancy(rate?.occupancy)}</span>
                        </div>

                        <div className="flex items-center gap-1">
                          <MaterialIcon icon="price_change" size={IconSizes.xxs} />
                          <span className="truncate">{rate?.name ?? rate?.displayName ?? ''}</span>
                        </div>

                        <RoomPolicyLines rate={rate} roomName={roomOption?.displayName} checkInDate={checkInDate} />
                      </div>
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </section>

      {propertyExtras.length > 0 ? (
        <section className="">
          <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Extras</h2>
          <ul className="mt-2 space-y-1 text-sm">
            {propertyExtras.map((extra) => {
              const label = extra.qty > 1 ? `${extra.label} × ${extra.qty}` : extra.label;
              const priceLabel =
                extra.amount == null
                  ? undefined
                  : extra.amount === 0
                    ? 'Free'
                    : formatPrice(extra.amount, extra.currency);
              return (
                <li key={extra.id} className="flex items-baseline justify-between gap-2">
                  <span>{label}</span>
                  {priceLabel ? <span>{priceLabel}</span> : null}
                </li>
              );
            })}
          </ul>
          <p className="mt-2 text-xs text-muted-foreground">
            These extras are settled directly with the hotel and are not included in your booking total.
          </p>
        </section>
      ) : null}

      {total ? (
        <section className="border-t border-border pt-4 space-y-1.5">
          {breakdown && (breakdown.totalTax > 0 || breakdown.totalFees > 0) ? (
            <>
              <BreakdownLine label="Subtotal" amount={breakdown.subtotal} currency={breakdown.currency} muted />
              {/* Itemize taxes (City / State / Lodging…) when Sabre breaks them out so high city/island taxes
                  are obvious; otherwise a single aggregate line. */}
              {breakdown.taxBreakdown && breakdown.taxBreakdown.length > 0 ? (
                breakdown.taxBreakdown.map((tax, i) => (
                  <BreakdownLine
                    key={`tax-${i}`}
                    label={tax.label ?? 'Tax'}
                    amount={tax.amount}
                    currency={tax.currency ?? breakdown.currency}
                    muted
                  />
                ))
              ) : breakdown.totalTax > 0 ? (
                <BreakdownLine label="Taxes" amount={breakdown.totalTax} currency={breakdown.currency} muted />
              ) : null}
              {/* Name each mandatory fee (e.g. "Resort fee") when Sabre itemized them; otherwise the aggregate. */}
              {breakdown.feeBreakdown && breakdown.feeBreakdown.length > 0 ? (
                breakdown.feeBreakdown.map((fee, i) => (
                  <BreakdownLine
                    key={`fee-${i}`}
                    label={fee.label ?? 'Fee'}
                    amount={fee.amount}
                    currency={fee.currency ?? breakdown.currency}
                    muted
                  />
                ))
              ) : breakdown.totalFees > 0 ? (
                <BreakdownLine label="Fees" amount={breakdown.totalFees} currency={breakdown.currency} muted />
              ) : null}
            </>
          ) : null}
          <div className="flex items-baseline justify-between pt-1">
            <span className="text-base font-medium">Total ({total.currency})</span>
            <span className="text-base font-semibold">{formatPrice(total.amount, total.currency)}</span>
          </div>

          {paymentSchedule.length > 0 ? <PaymentScheduleSection entries={paymentSchedule} /> : null}
        </section>
      ) : null}
    </div>
  );
}

/** Single muted line in the totals breakdown ("Subtotal $X" etc.). */
function BreakdownLine({
  label,
  amount,
  currency,
  muted,
}: {
  label: string;
  amount: number;
  currency: string;
  muted?: boolean;
}): React.ReactNode {
  return (
    <div className={`flex items-baseline justify-between text-sm ${muted ? 'text-muted-foreground' : ''}`}>
      <span>{label}</span>
      <span className="tabular-nums">{formatPrice(amount, currency)}</span>
    </div>
  );
}

function sortRoomsByName(
  rooms: HotelBookingRoom[],
  roomOptionsById: Map<string, RoomOption | undefined>,
): HotelBookingRoom[] {
  return rooms
    .map((room, index) => ({ room, index, name: roomOptionsById.get(room.roomOptionId)?.displayName ?? 'Room' }))
    .toSorted((a, b) => a.name.localeCompare(b.name) || a.index - b.index)
    .map(({ room }) => room);
}

function RoomPolicyLines({
  rate,
  roomName,
  checkInDate,
}: {
  rate: RoomRateModel | undefined;
  roomName?: string;
  checkInDate?: string;
}): React.ReactNode {
  if (!rate) return null;
  const cancellation = describeCancellationPolicy(rate, checkInDate);
  const deposit = describeDepositPolicy(rate);
  const guarantee = describeGuaranteePolicy(rate);
  const hasPolicies = !!(cancellation || deposit || guarantee);
  const rateName = rate.name ?? rate.displayName;

  return (
    <>
      <span className={cn('flex items-center gap-1', rate.refundStatus === 'refundable' && 'text-success')}>
        <MaterialIcon
          icon={
            rate.refundStatus === 'unconfirmed' ? 'schedule' : rate.isRefundable ? 'currency_exchange' : 'money_off'
          }
          size={IconSizes.xxs}
        />
        {rate.cancellationPolicyTitle}
      </span>
      {hasPolicies ? (
        <Dialog>
          <DialogTrigger asChild>
            <Button
              variant="link"
              className="@md:ml-auto text-2xs underline font-normal underline-offset-auto text-muted-foreground"
            >
              Full policies
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Booking policies</DialogTitle>
              {roomName || rateName ? (
                <p className="text-sm text-muted-foreground">{[roomName, rateName].filter(Boolean).join(' · ')}</p>
              ) : null}
            </DialogHeader>
            <CheckoutPolicyDetails rate={rate} checkInDate={checkInDate} />
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}

/**
 * Trip-level payment schedule rendered under the Total. Always shows the
 * combined cashflow — one line per distinct due date plus a final
 * "due at the property" balance line.
 */
function PaymentScheduleSection({ entries }: { entries: PaymentScheduleEntry[] }): React.ReactNode {
  return (
    <ul className="pt-4 mt-4 border-t space-y-1.5">
      {entries.map((entry, i) => (
        <PaymentScheduleLine key={i} entry={entry} />
      ))}
    </ul>
  );
}

function PaymentScheduleLine({ entry }: { entry: PaymentScheduleEntry }): React.ReactNode {
  const percent = entry.percent != null ? ` (${Math.round(entry.percent)}%)` : '';
  return (
    <li className={cn('flex items-baseline justify-between gap-2 text-sm text-muted-foreground')}>
      <span>
        {formatPaymentScheduleLabel(entry)}
        <span className="text-muted-foreground">{percent}</span>
      </span>
      <span className="tabular-nums">{formatPrice(entry.amount, entry.currency)}</span>
    </li>
  );
}
